import { WarningAmber } from "@mui/icons-material";
import { Box, Chip, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_SortingState,
} from "material-react-table";
import React, { useMemo, useState } from "react";
import { ResearchEntity } from "../../../types/researchWorkflow";
import { getScoreHex, getScoreCellBg } from "../shared/scoreColors";
import { selectVisibleDeselectAllProps } from "../shared/selectionProps";
import EvaluationScoreTooltip from "./EvaluationScoreTooltip";

interface ComparisonMatrixViewProps {
  entities: ResearchEntity[];
  criteria: Array<{
    id: string; // UUID from SpecCriterion
    criterion_id?: string; // Stable identifier
    label: string;
    weight: number;
  }>;
  isLoading?: boolean;
  onEntityClick?: (entityId: string) => void;
  onSelectEntity: (entityId: string) => void;
  onViewEvaluation?: (entity: ResearchEntity) => void;
  scenarioMode?: boolean;
  scenarioWeights?: Record<string, number>;
  onWeightChange?: (criterionId: string, weight: number) => void;
  // Shared with the table view so a selection survives switching views and
  // the toolbar actions (evaluate, move, export) work from either one.
  rowSelection: MRT_RowSelectionState;
  onRowSelectionChange: (
    updater:
      | MRT_RowSelectionState
      | ((old: MRT_RowSelectionState) => MRT_RowSelectionState),
  ) => void;
}

// Transparent versions of the badge color scale (success.dark / success.main / warning.main / error.main / error.dark)
const getCellBackground = getScoreCellBg;

const TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: 3,
      "& .MuiTooltip-arrow": {
        color: "background.paper",
        "&::before": {
          border: "1px solid",
          borderColor: "divider",
        },
      },
    },
  },
};

const getConfidenceLabel = (conf: number): string => {
  if (conf >= 0.8) return "High";
  if (conf >= 0.6) return "Medium";
  return "Low";
};

const getScoreColor15 = getScoreHex;

const calculateScenarioScore = (
  entity: ResearchEntity,
  criteria: Array<{ id: string; weight: number }>,
  scenarioWeights: Record<string, number>,
): number | undefined => {
  const evaluationData = entity.evaluationData as any;
  if (!evaluationData?.criteria_results) return undefined;

  // For unchanged criteria, use result.weight (the exact integer weight the backend used when
  // computing the stored score). This ensures bit-for-bit identical IEEE 754 results vs the
  // backend sum. Only apply scenario overrides for criteria the user has explicitly changed.
  let totalWeight = 0;
  let weightedSum = 0;
  for (const [criterionId, result] of Object.entries(evaluationData.criteria_results as Record<string, any>)) {
    if (result?.score !== undefined) {
      const specWeight = criteria.find((c) => c.id === criterionId)?.weight;
      const weight = criterionId in scenarioWeights
        ? scenarioWeights[criterionId]
        : (result.weight ?? specWeight ?? 1.0);
      weightedSum += result.score * weight;
      totalWeight += weight;
    }
  }
  if (totalWeight <= 0) return undefined;
  return weightedSum / totalWeight;
};

const ComparisonMatrixView: React.FC<ComparisonMatrixViewProps> = ({
  entities,
  criteria,
  isLoading = false,
  onEntityClick,
  onSelectEntity,
  onViewEvaluation,
  scenarioMode = false,
  scenarioWeights = {},
  onWeightChange,
  rowSelection,
  onRowSelectionChange,
}) => {
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "combinedScore", desc: true }]);

  const tableData = useMemo(
    () =>
      entities.map((e) => ({
        ...e,
        _scenarioScore: scenarioMode
          ? calculateScenarioScore(e, criteria, scenarioWeights)
          : undefined,
      })) as unknown as ResearchEntity[],
    [entities, criteria, scenarioMode, scenarioWeights],
  );

  const columns = useMemo<MRT_ColumnDef<ResearchEntity>[]>(() => {
    const baseColumns: MRT_ColumnDef<ResearchEntity>[] = [
      {
        accessorKey: "name",
        header: "Name",
        size: 250,
        muiTableHeadCellProps: {
          sx: {
            borderRight: "2px solid",
            borderRightColor: "divider",
            backgroundColor: "background.paper",
          },
        },
        muiTableBodyCellProps: {
          sx: {
            borderRight: "2px solid",
            borderRightColor: "divider",
            // Opaque: the column is pinned, and the colored score cells must
            // not shine through while scrolling underneath it.
            backgroundColor: "background.paper",
          },
        },
        Cell: ({ cell, row }) => (
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "primary.light",
                textDecoration: "underline",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onEntityClick) {
                onEntityClick(row.original.kg_entity_id);
              } else {
                onSelectEntity(row.original.id);
              }
            }}
          >
            {String(cell.getValue())}
          </Typography>
        ),
      },
      {
        id: "combinedScore",
        accessorFn: (row) =>
          scenarioMode
            ? (((row as any)._scenarioScore) ?? -1)
            : (row.evaluationData?.overall_score ?? -1),
        header: "Combined Score",
        size: 120,
        Header: scenarioMode
          ? () => (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <span>Combined Score</span>
                <Chip
                  label="scenario"
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 16, fontSize: "0.65rem", fontStyle: "italic", alignSelf: "flex-start" }}
                />
              </Box>
            )
          : undefined,
        muiTableHeadCellProps: {
          sx: {
            borderRight: "2px solid",
            borderRightColor: "divider",
          },
        },
        muiTableBodyCellProps: ({ row }) => {
          const displayScore = scenarioMode
            ? calculateScenarioScore(row.original, criteria, scenarioWeights)
            : row.original.evaluationData?.overall_score;

          return {
            sx: {
              borderRight: "2px solid",
              borderRightColor: "divider",
              backgroundColor: getCellBackground(displayScore),
            },
          };
        },
        Cell: ({ row }) => {
          const entity = row.original;
          const evaluationData = entity.evaluationData as any;
          const displayScore = scenarioMode
            ? calculateScenarioScore(entity, criteria, scenarioWeights)
            : evaluationData?.overall_score;

          if (displayScore === undefined) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                -
              </Typography>
            );
          }

          const inner = (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                cursor: onViewEvaluation ? "pointer" : "default",
                "&:hover": onViewEvaluation ? { color: "primary.light" } : {},
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onViewEvaluation) onViewEvaluation(entity);
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textAlign: "center" }}
              >
                {displayScore.toFixed(1)}
              </Typography>
              {!scenarioMode && evaluationData?.spec_version_changed && (
                <WarningAmber sx={{ fontSize: 14, color: "warning.main" }} />
              )}
            </Box>
          );

          return scenarioMode ? (
            inner
          ) : (
            <EvaluationScoreTooltip evaluationData={evaluationData}>
              {inner}
            </EvaluationScoreTooltip>
          );
        },
      },
    ];

    const criterionColumns: MRT_ColumnDef<ResearchEntity>[] = criteria.map(
      (criterion) => ({
        id: `criterion_${criterion.id}`,
        header: criterion.label,
        Header: scenarioMode
          ? () => (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box
                  sx={{
                    height: 32,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    lineHeight: 1.3,
                  }}
                >
                  {criterion.label}
                </Box>
                <Select
                  size="small"
                  value={scenarioWeights[criterion.id] ?? criterion.weight}
                  onChange={(e) => onWeightChange?.(criterion.id, Number(e.target.value))}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontSize: "0.75rem", height: 26, width: 58 }}
                >
                  {[0, 1, 2, 3, 4, 5].map((w) => (
                    <MenuItem key={w} value={w} sx={{ fontSize: "0.75rem" }}>
                      {w}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )
          : undefined,
        size: 150,
        accessorFn: (row) => {
          const evaluationData = row.evaluationData as any;
          const criterionResult =
            evaluationData?.criteria_results?.[criterion.id];
          return criterionResult?.score ?? -1; // Return -1 for missing scores so they sort to bottom
        },
        muiTableBodyCellProps: ({ row }) => {
          const evaluationData = row.original.evaluationData as any;
          const criterionResult =
            evaluationData?.criteria_results?.[criterion.id];
          const score = criterionResult?.score;

          return {
            sx: {
              backgroundColor: getCellBackground(score),
            },
          };
        },
        Cell: ({ row }: any) => {
          const entity = row.original;
          const evaluationData = entity.evaluationData as any;

          // Access criterion result directly using UUID
          const criterionResult =
            evaluationData?.criteria_results?.[criterion.id];

          if (!criterionResult) {
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                -
              </Typography>
            );
          }

          const score = criterionResult.score;
          const confidence = criterionResult.confidence;
          const evidenceCount = criterionResult.evidence_count ?? 0;
          const summary = criterionResult.one_sentence_summary || "";

          const critScoreColor = getScoreColor15(score);
          const critNormalized = score / 5;
          const critCircumference = 2 * Math.PI * 20;

          return (
            <Tooltip
              title={
                <Box sx={{ maxWidth: 400, p: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <svg
                        width="48"
                        height="48"
                        style={{ transform: "rotate(-90deg)" }}
                      >
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          fill="none"
                          stroke="rgba(255, 255, 255, 0.1)"
                          strokeWidth="4"
                        />
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          fill="none"
                          stroke={critScoreColor}
                          strokeWidth="4"
                          strokeDasharray={`${critCircumference}`}
                          strokeDashoffset={`${critCircumference * (1 - critNormalized)}`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            color: critScoreColor,
                          }}
                        >
                          {score.toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.25 }}
                      >
                        {criterion.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mb: 0.25,
                        }}
                      >
                        {getConfidenceLabel(confidence)} confidence
                        {evidenceCount > 0 &&
                          ` · ${evidenceCount} claim${evidenceCount !== 1 ? "s" : ""}`}
                      </Typography>
                    </Box>
                  </Box>
                  {summary && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", lineHeight: 1.4 }}
                      >
                        {summary.length > 200
                          ? summary.substring(0, 200).trim() + "..."
                          : summary}
                      </Typography>
                    </Box>
                  )}
                  {criterionResult.is_stale && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        color: "warning.main",
                      }}
                    >
                      <WarningAmber sx={{ fontSize: 14 }} />
                      <Typography variant="caption">
                        {criterionResult.stale_reason === "rubric_changed"
                          ? "Rubric changed since this evaluation"
                          : "Criterion changed since this evaluation"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
              arrow
              placement="top"
              componentsProps={TOOLTIP_PROPS}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  cursor: onViewEvaluation ? "pointer" : "default",
                  "&:hover": onViewEvaluation ? { color: "primary.light" } : {},
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onViewEvaluation) onViewEvaluation(entity);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, textAlign: "center" }}
                >
                  {score.toFixed(1)}
                </Typography>
                {criterionResult.is_stale && (
                  <WarningAmber sx={{ fontSize: 14, color: "warning.main" }} />
                )}
              </Box>
            </Tooltip>
          );
        },
      }),
    );

    return [...baseColumns, ...criterionColumns];
  }, [criteria, onEntityClick, onSelectEntity, onViewEvaluation, scenarioMode, scenarioWeights, onWeightChange]);

  return (
    <MaterialReactTable
      columns={columns}
      data={tableData}
      enableColumnResizing={false}
      enableSorting={true}
      enableStickyHeader
      enableBottomToolbar
      enableTopToolbar={false}
      enableRowSelection
      // Select adds the visible page; a fully-selected page clears the whole
      // id-keyed selection, including rows parked on other pages.
      muiSelectAllCheckboxProps={selectVisibleDeselectAllProps(
        onRowSelectionChange,
      )}
      getRowId={(row) => row.id}
      onRowSelectionChange={onRowSelectionChange}
      enableColumnFilters={false}
      enablePinning
      displayColumnDefOptions={{
        "mrt-row-select": {
          muiTableHeadCellProps: {
            sx: { backgroundColor: "background.paper" },
          },
          muiTableBodyCellProps: {
            sx: { backgroundColor: "background.paper" },
          },
        },
      }}
      state={{
        isLoading: isLoading,
        showProgressBars: isLoading,
        sorting,
        rowSelection,
      }}
      onSortingChange={setSorting}
      initialState={{
        density: "compact",
        // Keep the selection + name columns visible while the criterion
        // columns scroll horizontally.
        columnPinning: { left: ["mrt-row-select", "name"] },
      }}
      muiTablePaperProps={{
        elevation: 0,
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
        },
      }}
      muiTableContainerProps={{
        sx: { flex: 1 },
      }}
      muiBottomToolbarProps={{
        sx: { backgroundColor: "#1e1e1e" },
      }}
    />
  );
};

export default ComparisonMatrixView;
